import { IconTypes } from '@eika/icon';
import { RichIconTypes } from '@eika/rich-icon';

import { SvgCardType } from '../../components/svg-card/svg-card-types';
import { KategoriSvarId } from '../../landingsside/sporsmal-og-svar';
import {
    AttentionMessageSporsmalDTO,
    AttentionMessageType,
    BilmerkeDTO,
    BlindveiSporsmalDTO,
    CardSporsmalDTO,
    CardType,
    CheckboxSporsmalDTO,
    CheckboxType,
    DateAsString,
    DatosporsmalDTO,
    FilopplastingSporsmalDTO,
    ForsikringDekningDTO,
    ForsikringObjekterCheckboxSporsmalDTO,
    ForsikringsnummerSporsmalDTO,
    ForsikringsporsmalDTO,
    ForsikringsprodukterPriority,
    InformasjonSporsmalDTO,
    KontaktInfoInvolvertSporsmalDTO,
    KontaktinfosporsmalDTO,
    KontaktInfosporsmalFieldLabels,
    KredittkortnummerSporsmalDTO,
    ModalSporsmallDTO,
    ModalType,
    PostnummerSporsmalDTO,
    RadiosporsmalDTO,
    RadioType,
    SelectAlternativDTO,
    SelectSporsmalDTO,
    SimpleKontaktinfosporsmalDTO,
    SkadedatoAndForsikringSporsmalDTO,
    SkadelidtInfoSporsmalDTO,
    SkadelidtInfoSporsmalFieldLabels,
    SkadenummerSporsmalDTO,
    SporsmalId,
    SporsmalKategori,
    SporsmalListDTO,
    SporsmalsflytDTO,
    SporsmalsGruppeDTO,
    SporsmalSideDTO,
    SporsmalType,
    SvarId,
    TallsporsmalDTO,
    TallValidationType,
    TekstsporsmalDTO,
    TextInputSize,
    UtleggHeaderTekst,
    UtleggslinjeDTO,
    UtleggSporsmalDTO,
    UtleggType,
    UtsattForgreiningSporsmalDTO,
    Valideringstype,
    VerkstedSporsmalDTO,
} from '../../types/generated-types';
import { LinkItem } from '../link-item/link-item-types';
import { getTallValideringsTypeFrom } from '../tall/tall-validation';
import { CurrentUtleggslinje } from './current-steg';
import { TriggeredBy } from './triggeredby-types';
import { getValideringsTypeFrom } from './valideringstype';

export interface Sporsmalsflyt {
    sporsmalsList: Sporsmal[];
    triggered: SporsmalsGruppe[];
}

export interface RadioAlternativ {
    id: SvarId | KategoriSvarId;
    tekst: string;
    undertekst?: string;
    feilmelding?: Feilmelding;
    oppsummeringsInfo?: string;
}

export interface CheckboxAlternativ {
    id: SvarId;
    tekst: string;
}

export interface ForsikringAlternativ {
    id: ForsikringDekningDTO;
    tekst: string;
    forsikringsnummer: string;
    forsikringversjon: number;
    undertekst?: string;
    forsikringsperiode?: DekningForsikringsperiode;
}

export interface Feilmelding {
    tekst: string;
    tittel: string;
}

export interface DekningForsikringsperiode {
    from: DateAsString;
    to: DateAsString;
}

interface SporsmalBase {
    id: SporsmalId;
    triggeredBy?: TriggeredBy;
    sporsmalKategori: SporsmalKategori;
}

export interface Radiosporsmal extends SporsmalBase {
    type: SporsmalType.Radio;
    tekst: string;
    alternativer: RadioAlternativ[];
    info?: string;
    ingress?: string;
    radioType: RadioType;
}

export type SelectAlternativ = SelectAlternativDTO;

export interface SelectSporsmal extends SporsmalBase {
    type: SporsmalType.Select;
    alternativer: SelectAlternativ[];
    defaultOptionText: string;
    ingress?: string;
    tekst: string;
}

export interface Checkboxsporsmal extends SporsmalBase {
    type: SporsmalType.Checkbox;
    tekst: string;
    alternativer: CheckboxAlternativ[];
    ingress?: string;
    checkboxType: CheckboxType;
}

export interface Forsikringsporsmal extends SporsmalBase {
    type: SporsmalType.Forsikring;
    tekst: string;
    ingenForsikringHeader: string;
    ingenForsikringTekst: string;
    info?: string;
    ingress?: string;
    finnerIkkeRiktigForsikringAlternativTekst?: string;
    forsikringsprodukterPriority?: ForsikringsprodukterPriority;
}

export interface ForsikringsnummerSporsmal extends SporsmalBase {
    type: SporsmalType.Forsikringsnummer;
    tekst: string;
    ingress?: string;
}

export interface ForsikringObjekterCheckboxSporsmal extends SporsmalBase {
    type: SporsmalType.ForsikringObjekterCheckbox;
    tekst: string;
    ingress?: string;
}

export interface SkadedatoAndForsikringSporsmal extends SporsmalBase {
    type: SporsmalType.SkadedatoAndForsikring;
    id: SporsmalId;
    skadedatoTittel: string;
    skadedatoIngress?: string;
    forsikringTittel: string;
    forsikringIngress?: string;
    finnerIkkeRiktigForsikringAlternativTekst?: string;
    forsikringsprodukterPriority?: ForsikringsprodukterPriority;
    showFeilmelding: boolean;
}

export interface Tekstsporsmal extends SporsmalBase {
    type: SporsmalType.Tekst;
    tekst: string;
    ingress?: string;
    placeholderText?: string;
    valideringstype: Valideringstype;
    size: TextInputSize;
    maxNumberOfCharacters?: number;
}

export interface Datosporsmal extends SporsmalBase {
    type: SporsmalType.Dato;
    tittel: string;
    ingress: string;
    allowFutureDate: boolean;
}

export interface Tallsporsmal extends SporsmalBase {
    type: SporsmalType.Tall;
    tittel: string;
    ingress?: string;
    maleenhet: string;
    tallvalidering: TallValidationType;
}

export interface Kontaktinfosporsmal extends SporsmalBase {
    type: SporsmalType.Kontaktinfo;
    tekst: string;
    tittel: string;
    askForBankkontonummer: boolean;
    kontaktInfosporsmalFieldLabels: KontaktInfosporsmalFieldLabels;
}

export interface SkadelidtInfoSporsmal extends SporsmalBase {
    type: SporsmalType.SkadelidtInfo;
    tittel: string;
    infoTekstPrimary: string;
    infoTextSecondary: string;
    skadelidtInfoSporsmalFieldLabels: SkadelidtInfoSporsmalFieldLabels;
}

export interface KontaktInfoInvolvertSporsmal extends SporsmalBase {
    type: SporsmalType.KontaktInfoInvolvert;
    tittel: string;
    ingress: string;
}

export interface SimpleKontaktinfosporsmal extends SporsmalBase {
    type: SporsmalType.SimpleKontaktinfo;
    tittel: string;
    ingress: string;
}

export interface Filopplastingsporsmal extends SporsmalBase {
    type: SporsmalType.Filopplasting;
    tittel: string;
    tekst: string;
    ingress: string;
    valideringstype: Valideringstype;
    withDirectUpload: boolean;
}

export interface SkadenummerSporsmal extends SporsmalBase {
    type: SporsmalType.Skadenummer;
    tekst: string;
    ingress?: string;
}

export interface Utleggsporsmal extends SporsmalBase {
    type: SporsmalType.Utlegg;
    tittel: string;
    ingress: string;
    toggles: UtleggToggles;
    utleggType: UtleggType;
    headerTekst: UtleggHeaderTekst;
}

export type Bilmerke = BilmerkeDTO;

export interface Verkstedsporsmal extends SporsmalBase {
    type: SporsmalType.Verksted;
    tittel: string;
    bilmerker: Bilmerke[];
}

export interface Informasjonsporsmal extends SporsmalBase {
    type: SporsmalType.Informasjon;
    tekst: string;
    ingress: string;
    svgCard: SvgCardType;
}

export interface AttentionMessageSporsmal extends SporsmalBase {
    type: SporsmalType.AttentionMessage;
    tekst: string;
    attentionMessageType: AttentionMessageType;
    iconType?: IconTypes;
    linkItem?: LinkItem;
    tittel?: string;
}

export interface CardSporsmal extends SporsmalBase {
    type: SporsmalType.Card;
    tittel: string;
    text: string;
    cardType: CardType;
    richIconType?: RichIconTypes;
    linkItem?: LinkItem;
}

export interface Postnummersporsmal extends SporsmalBase {
    type: SporsmalType.Postnummer;
    tekst: string;
}

export interface ModalSporsmal extends SporsmalBase {
    type: SporsmalType.Modal;
    modalType: ModalType;
}

export interface Blindveisporsmal extends SporsmalBase {
    type: SporsmalType.Blindvei;
    tekst: string;
    ingress: string;
}

export interface UtsattForgreiningsporsmal extends SporsmalBase {
    type: SporsmalType.UtsattForgreining;
}

export interface SporsmalSide extends SporsmalBase {
    type: SporsmalType.SporsmalSide;
    tittel: string;
    ingress?: string;
    showTitle: boolean;
    svgCard: SvgCardType;
    sporsmalsflyt: Sporsmalsflyt;
}

export interface SporsmalList extends SporsmalBase {
    type: SporsmalType.SporsmalList;
    tittel: string;
    ingress: string;
    sporsmalsflyt: Sporsmalsflyt;
    tittelFeltId: string;
    subTittelFeltId: string;
    addCollectionButtonLabel: string;
    removeButtonLabel: string;
}

export interface KredtittkortnummerSporsmal extends SporsmalBase {
    type: SporsmalType.Kredittkortnummer;
    tekst: string;
    ingress?: string;
}

export type Sporsmal =
    | Radiosporsmal
    | Checkboxsporsmal
    | Forsikringsporsmal
    | SkadedatoAndForsikringSporsmal
    | Tekstsporsmal
    | Datosporsmal
    | Tallsporsmal
    | Kontaktinfosporsmal
    | KontaktInfoInvolvertSporsmal
    | SimpleKontaktinfosporsmal
    | SkadelidtInfoSporsmal
    | Filopplastingsporsmal
    | Utleggsporsmal
    | Verkstedsporsmal
    | Informasjonsporsmal
    | Blindveisporsmal
    | UtsattForgreiningsporsmal
    | SporsmalSide
    | SporsmalList
    | SkadenummerSporsmal
    | AttentionMessageSporsmal
    | CardSporsmal
    | Postnummersporsmal
    | SelectSporsmal
    | ForsikringsnummerSporsmal
    | ForsikringObjekterCheckboxSporsmal
    | KredtittkortnummerSporsmal
    | ModalSporsmal;

export enum IdentifikatorType {
    Fodselsnummer = 'Fodselsnummer',
    Organisasjonsnummer = 'Organisasjonsnummer',
}

export const toUtleggslinjeDTO = (linje: CurrentUtleggslinje): UtleggslinjeDTO => ({
    beskrivelse: linje.beskrivelse,
    dato: linje.dato ? new Date(linje.dato).toISOString() : undefined,
    refundert: Number(linje.refundert),
    utgift: Number(linje.utgift),
    utleggType: linje.utleggType,
    dagensPris: Number(linje.dagensPris),
});

export interface UtleggToggles {
    includeRefusjon: boolean;
    includeDato: boolean;
    includeAddNewUtleggslinjeButton: boolean;
}

export type SporsmalDTO =
    | RadiosporsmalDTO
    | CheckboxSporsmalDTO
    | TekstsporsmalDTO
    | DatosporsmalDTO
    | TallsporsmalDTO
    | KontaktinfosporsmalDTO
    | KontaktInfoInvolvertSporsmalDTO
    | SimpleKontaktinfosporsmalDTO
    | SkadelidtInfoSporsmalDTO
    | FilopplastingSporsmalDTO
    | UtleggSporsmalDTO
    | VerkstedSporsmalDTO
    | InformasjonSporsmalDTO
    | BlindveiSporsmalDTO
    | UtsattForgreiningSporsmalDTO
    | SporsmalSideDTO
    | SporsmalListDTO
    | SkadenummerSporsmalDTO
    | AttentionMessageSporsmalDTO
    | CardSporsmalDTO
    | ForsikringsporsmalDTO
    | SkadedatoAndForsikringSporsmalDTO
    | PostnummerSporsmalDTO
    | SelectSporsmalDTO
    | ForsikringsnummerSporsmalDTO
    | ForsikringObjekterCheckboxSporsmalDTO
    | KredittkortnummerSporsmalDTO
    | ModalSporsmallDTO;

export const convertFromSporsmalDTO = (sporsmalDTO: SporsmalDTO): Sporsmal => {
    switch (sporsmalDTO.sporsmaltype) {
        case SporsmalType.Radio: {
            return {
                type: SporsmalType.Radio,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                alternativer: sporsmalDTO.alternativer,
                info: sporsmalDTO.info,
                ingress: sporsmalDTO.ingress,
                radioType: sporsmalDTO.radioType,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        }
        case SporsmalType.Checkbox:
            return {
                type: SporsmalType.Checkbox,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                alternativer: sporsmalDTO.alternativer,
                ingress: sporsmalDTO.ingress,
                checkboxType: sporsmalDTO.checkboxType,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Forsikring:
            return {
                type: SporsmalType.Forsikring,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                ingenForsikringHeader: sporsmalDTO.ingenForsikringHeader,
                ingenForsikringTekst: sporsmalDTO.ingenForsikringTekst,
                info: sporsmalDTO.info,
                ingress: sporsmalDTO.ingress,
                finnerIkkeRiktigForsikringAlternativTekst: sporsmalDTO.finnerIkkeRiktigForsikringAlternativTekst,
                forsikringsprodukterPriority: sporsmalDTO.forsikringsprodukterPriority,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.SkadedatoAndForsikring:
            return {
                type: SporsmalType.SkadedatoAndForsikring,
                id: sporsmalDTO.id,
                skadedatoTittel: sporsmalDTO.skadedatoTittel,
                skadedatoIngress: sporsmalDTO.skadedatoIngress,
                forsikringTittel: sporsmalDTO.forsikringTittel,
                forsikringIngress: sporsmalDTO.forsikringIngress,
                finnerIkkeRiktigForsikringAlternativTekst: sporsmalDTO.finnerIkkeRiktigForsikringAlternativTekst,
                forsikringsprodukterPriority: sporsmalDTO.forsikringsprodukterPriority,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
                showFeilmelding: sporsmalDTO.showFeilmelding,
            };

        case SporsmalType.Forsikringsnummer:
            return {
                type: SporsmalType.Forsikringsnummer,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Tekst:
            return {
                type: SporsmalType.Tekst,
                id: sporsmalDTO.id,
                ingress: sporsmalDTO.ingress,
                placeholderText: sporsmalDTO.placeholderText,
                tekst: sporsmalDTO.tekst,
                valideringstype: getValideringsTypeFrom(sporsmalDTO.valideringstype),
                size: sporsmalDTO.size,
                maxNumberOfCharacters: sporsmalDTO.maxNumberOfCharacters,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Dato:
            return {
                type: SporsmalType.Dato,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                allowFutureDate: sporsmalDTO.allowFutureDate,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Tall:
            return {
                type: SporsmalType.Tall,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                maleenhet: sporsmalDTO.maleenhet,
                tallvalidering: getTallValideringsTypeFrom(sporsmalDTO.tallValidation),
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Kontaktinfo:
            return {
                type: SporsmalType.Kontaktinfo,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tekst,
                tekst: sporsmalDTO.tekst,
                askForBankkontonummer: sporsmalDTO.askForBankkontonummer,
                kontaktInfosporsmalFieldLabels: sporsmalDTO.kontaktInfosporsmalFieldLabels,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.KontaktInfoInvolvert:
            return {
                type: SporsmalType.KontaktInfoInvolvert,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.SimpleKontaktinfo:
            return {
                type: SporsmalType.SimpleKontaktinfo,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.SkadelidtInfo:
            return {
                type: SporsmalType.SkadelidtInfo,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                infoTekstPrimary: sporsmalDTO.infoTekstPrimary,
                infoTextSecondary: sporsmalDTO.infoTextSecondary,
                skadelidtInfoSporsmalFieldLabels: sporsmalDTO.skadelidtInfoSporsmalFieldLabels,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Filopplasting:
            return {
                type: SporsmalType.Filopplasting,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                tekst: sporsmalDTO.dropFileText,
                valideringstype: getValideringsTypeFrom(sporsmalDTO.valideringstype),
                withDirectUpload: sporsmalDTO.withDirectUpload,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Utlegg:
            return {
                type: SporsmalType.Utlegg,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                toggles: sporsmalDTO.utleggToggles,
                utleggType: sporsmalDTO.utleggType,
                headerTekst: sporsmalDTO.headerTekst,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Verksted:
            return {
                type: SporsmalType.Verksted,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                bilmerker: sporsmalDTO.bilmerker,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Informasjon:
            return {
                type: SporsmalType.Informasjon,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                svgCard: sporsmalDTO.svgCard as unknown as SvgCardType,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Blindvei:
            return {
                type: SporsmalType.Blindvei,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.UtsattForgreining:
            return {
                type: SporsmalType.UtsattForgreining,
                id: sporsmalDTO.id,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.SporsmalSide:
            return {
                type: SporsmalType.SporsmalSide,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                showTitle: sporsmalDTO.showTitle,
                svgCard: sporsmalDTO.svgCard as unknown as SvgCardType,
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                sporsmalsflyt: convertFromSporsmalsflytDTO(sporsmalDTO.sporsmalsflyt),
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.SporsmalList:
            return {
                type: SporsmalType.SporsmalList,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                ingress: sporsmalDTO.ingress,
                tittelFeltId: sporsmalDTO.tittelFeltId,
                subTittelFeltId: sporsmalDTO.subTittelFeltId,
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                sporsmalsflyt: convertFromSporsmalsflytDTO(sporsmalDTO.sporsmalsflyt),
                addCollectionButtonLabel: sporsmalDTO.addCollectionButtonLabel,
                removeButtonLabel: sporsmalDTO.removeButtonLabel,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };

        case SporsmalType.Skadenummer:
            return {
                type: SporsmalType.Skadenummer,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.AttentionMessage:
            return {
                type: SporsmalType.AttentionMessage,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                attentionMessageType: sporsmalDTO.attentionMessageType,
                iconType: sporsmalDTO.iconType as IconTypes,
                linkItem: sporsmalDTO.linkItem as LinkItem,
                tittel: sporsmalDTO.tittel,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Card:
            return {
                type: SporsmalType.Card,
                id: sporsmalDTO.id,
                tittel: sporsmalDTO.tittel,
                text: sporsmalDTO.text,
                linkItem: sporsmalDTO.linkItem as LinkItem,
                cardType: sporsmalDTO.cardType,
                richIconType: (sporsmalDTO.richIconType as RichIconTypes) || undefined,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Postnummer:
            return {
                type: SporsmalType.Postnummer,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Select:
            return {
                type: SporsmalType.Select,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                alternativer: sporsmalDTO.alternativer,
                defaultOptionText: sporsmalDTO.defaultOptionText,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.ForsikringObjekterCheckbox:
            return {
                type: SporsmalType.ForsikringObjekterCheckbox,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Kredittkortnummer:
            return {
                type: SporsmalType.Kredittkortnummer,
                id: sporsmalDTO.id,
                tekst: sporsmalDTO.tekst,
                ingress: sporsmalDTO.ingress,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
            };
        case SporsmalType.Modal:
            return {
                type: SporsmalType.Modal,
                id: sporsmalDTO.id,
                sporsmalKategori: sporsmalDTO.sporsmalKategori,
                modalType: sporsmalDTO.modalType,
            };
        default:
            return null;
    }
};

const addTriggeredByToSporsmal = (sporsmal: Sporsmal, triggeredBy: TriggeredBy): SporsmalGruppeSporsmal => ({
    ...sporsmal,
    triggeredBy,
});

type SporsmalGruppeSporsmal = Sporsmal & { triggeredBy: TriggeredBy };

export interface SporsmalsGruppe {
    triggeredBy: TriggeredBy;
    sporsmalsList: SporsmalGruppeSporsmal[];
}

export const convertFromSporsmalsGruppeDTO = (sporsmalsGruppeDTO: SporsmalsGruppeDTO): SporsmalsGruppe[] => {
    return sporsmalsGruppeDTO.triggered.map((triggeredByDTOUnion) => ({
        triggeredBy: triggeredByDTOUnion,
        sporsmalsList: sporsmalsGruppeDTO.sporsmalList
            .map(convertFromSporsmalDTO)
            .map((sporsmal) => addTriggeredByToSporsmal(sporsmal, triggeredByDTOUnion)),
    }));
};

export const convertFromSporsmalsflytDTO = (sporsmalsflytDTO: SporsmalsflytDTO): Sporsmalsflyt => ({
    sporsmalsList: sporsmalsflytDTO.sporsmal.map(convertFromSporsmalDTO),
    triggered: [].concat(...sporsmalsflytDTO.triggered.map(convertFromSporsmalsGruppeDTO)),
});
